import type { ReactNode } from "react";

type BodyMark = { attrs?: { href?: string }; type?: string };
type BodyNode = {
  attrs?: { id?: string; label?: string };
  content?: BodyNode[];
  marks?: BodyMark[];
  text?: string;
  type?: string;
};

export function CommentBody({ body }: { body: unknown }) {
  const doc = body as BodyNode | null;
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) return null;
  return <div className="flex min-w-0 flex-col gap-1.5 text-sm leading-relaxed [overflow-wrap:anywhere]">{doc.content.map(renderBlock)}</div>;
}

function renderBlock(node: BodyNode, index: number): ReactNode {
  const children = node.content ?? [];

  if (node.type === "paragraph") {
    return <p className="min-h-[1.25em]" key={index}>{children.map(renderInline)}</p>;
  }
  if (node.type === "codeBlock") {
    return (
      <pre className="overflow-x-auto rounded-md bg-muted/60 px-3 py-2 font-mono text-xs leading-relaxed" key={index}>
        <code>{children.map((child) => child.text ?? "").join("")}</code>
      </pre>
    );
  }
  if (node.type === "blockquote") {
    return <blockquote className="border-l-2 border-border pl-3 text-muted-foreground" key={index}>{children.map(renderBlock)}</blockquote>;
  }
  if (node.type === "bulletList") {
    return <ul className="flex list-disc flex-col gap-0.5 pl-5" key={index}>{children.map(renderBlock)}</ul>;
  }
  if (node.type === "orderedList") {
    return <ol className="flex list-decimal flex-col gap-0.5 pl-5" key={index}>{children.map(renderBlock)}</ol>;
  }
  if (node.type === "listItem") {
    return <li key={index}>{children.map(renderBlock)}</li>;
  }
  return null;
}

function renderInline(node: BodyNode, index: number): ReactNode {
  if (node.type === "hardBreak") {
    return <br key={index} />;
  }
  if (node.type === "mention") {
    return (
      <span className="rounded bg-primary/10 px-1 font-medium text-primary" key={index}>
        @{node.attrs?.label || "member"}
      </span>
    );
  }
  if (node.type !== "text" || typeof node.text !== "string") {
    return null;
  }

  let content: ReactNode = node.text;
  for (const mark of node.marks ?? []) {
    if (mark.type === "bold") content = <strong>{content}</strong>;
    if (mark.type === "italic") content = <em>{content}</em>;
    if (mark.type === "strike") content = <s>{content}</s>;
    if (mark.type === "code") content = <code className="rounded bg-muted/70 px-1 py-0.5 font-mono text-[0.85em]">{content}</code>;
    if (mark.type === "link" && mark.attrs?.href) {
      content = (
        <a className="text-primary underline underline-offset-2 hover:opacity-80" href={mark.attrs.href} rel="noreferrer" target="_blank">
          {content}
        </a>
      );
    }
  }
  return <span key={index}>{content}</span>;
}

export function commentBodyText(body: unknown): string {
  const doc = body as BodyNode | null;
  if (!doc || !Array.isArray(doc.content)) return "";
  const parts: string[] = [];
  collectText(doc.content, parts);
  return parts.join("").replace(/\n+/g, " ").trim();
}

function collectText(nodes: BodyNode[], parts: string[]) {
  for (const node of nodes) {
    if (node.type === "text" && node.text) parts.push(node.text);
    if (node.type === "mention") parts.push(`@${node.attrs?.label ?? ""}`);
    if (node.type === "hardBreak" || node.type === "paragraph") parts.push("\n");
    if (node.content) collectText(node.content, parts);
  }
}
